import { jsPDF } from "jspdf";
import { Canvg } from "canvg";

const FLOOR_FILES = [
  { key: "ground", label: "Ground floor", url: "/floors/floor_ground 2.svg" },
  { key: "first", label: "First floor", url: "/floors/floor_1 2.svg" },
  { key: "second", label: "Second floor", url: "/floors/floor_2 2.svg" },
];

const BOOKING_SHEET_LOGO_URL = "/booking-sheet-logo.png";
const BOOKING_PRICE_MODEL = {
  extra_badge_price: 2.5,
  polo_shirt_price: 15.0,
  rally_full_weekend_price: 35.0,
  child_rally_full_weekend_price: 0.0,
  camp_pitch_weekend_price: 120.0,
  van_pitch_weekend_price: 150.0,
  room_1_bed_price: 220.0,
  room_2_bed_price: 280.0,
  room_3_bed_price: 400.0,
  room_4_bed_price: 500.0,
  food_package_price: 50.0,
  bbq_pass_price: 10.0,
};
const SHIRT_SIZES = ["S", "M", "L", "XL", "XXL"];

const COLORS = {
  navy: [33, 55, 85],
  gold: [218, 151, 36],
  black: [0, 0, 0],
  border: [204, 214, 226],
  soft: [244, 247, 251],
  text: [20, 37, 64],
  white: [255, 255, 255],
};

function formatCurrency(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function normaliseAccommodation(value) {
  if (!value) return "none";
  return String(value).toLowerCase();
}

function buildRoomsSummary(roomsBooked) {
  if (!Array.isArray(roomsBooked) || roomsBooked.length === 0) return "-";

  return roomsBooked
    .map((r) => {
      const roomName = r?.roomName || "Unknown room";
      const capacity = r?.capacity ? ` (${r.capacity})` : "";
      return `${roomName}${capacity}`;
    })
    .join(", ");
}

function buildMerchSummary(merch) {
  merch = normaliseObject(merch);
  if (!merch || typeof merch !== "object") return "-";

  const bits = [];

  const badges = Number(merch.badges || 0);
  if (badges > 0) {
    bits.push(`${badges} badge${badges === 1 ? "" : "s"}`);
  }

  const bbqPasses = Number(merch.bbqPasses || 0);
  if (bbqPasses > 0) {
    bits.push(`${bbqPasses} BBQ pass${bbqPasses === 1 ? "" : "es"}`);
  }

  const shirts = merch.shirts || {};
  const shirtBits = ["S", "M", "L", "XL", "XXL"]
    .map((size) => {
      const qty = Number(shirts?.[size] || 0);
      return qty > 0 ? `${qty}x${size}` : null;
    })
    .filter(Boolean);

  if (shirtBits.length > 0) {
    bits.push(`Shirts: ${shirtBits.join(", ")}`);
  }

  return bits.length ? bits.join(" - ") : "-";
}

function getMealCovers(booking) {
  const explicit = Number(normaliseObject(booking?.merch).mealCovers);
  if (Number.isFinite(explicit) && explicit >= 0) return Math.floor(explicit);
  return booking?.meals_package === "all" ? Number(booking?.total_people || 0) : 0;
}

function getCoeliacMealCovers(booking) {
  const explicit = Number(normaliseObject(booking?.merch).coeliacMealCovers);
  if (!Number.isFinite(explicit) || explicit < 0) return 0;
  return Math.min(Math.floor(explicit), getMealCovers(booking));
}

function buildMealsSummary(booking) {
  const covers = getMealCovers(booking);
  const coeliacCovers = getCoeliacMealCovers(booking);
  if (covers <= 0) return "None";
  return `${covers} meal package${covers === 1 ? "" : "s"}${
    coeliacCovers > 0 ? ` (${coeliacCovers} coeliac)` : ""
  }`;
}

function normaliseObject(value, fallback = {}) {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

function normaliseArray(value) {
  if (Array.isArray(value)) return value;

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function uniqueNames(names) {
  const seen = new Set();
  return names
    .map((name) => String(name || "").trim())
    .filter((name) => {
      if (!name) return false;
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function attendeeNames(attendees, type) {
  const aliases = type === "child" ? ["child", "kid", "children"] : [type];
  return normaliseArray(attendees)
    .filter((a) => aliases.includes(String(a?.type || "").toLowerCase()))
    .map((a) => a?.name)
    .filter(Boolean);
}

function normaliseNameList(value) {
  return normaliseArray(value).filter((name) => String(name || "").trim());
}

function buildAdultNames(booking, fullName) {
  const adultNames = uniqueNames([
    fullName === "-" ? "" : fullName,
    ...attendeeNames(booking.attendees, "adult"),
    ...normaliseNameList(booking.adult_names || booking.adultNames),
  ]);

  const adultCount = Number(booking.adult_count);
  if (Number.isFinite(adultCount) && adultCount > adultNames.length) {
    const missing = adultCount - adultNames.length;
    adultNames.push(`+ ${missing} adult${missing === 1 ? "" : "s"}`);
  }

  return adultNames;
}

function buildChildNames(booking) {
  const childNames = uniqueNames([
    ...attendeeNames(booking.attendees, "child"),
    ...normaliseNameList(booking.kid_names || booking.kidNames || booking.child_names),
  ]);

  const childCount = Number(booking.child_count);
  if (Number.isFinite(childCount) && childCount > childNames.length) {
    const missing = childCount - childNames.length;
    childNames.push(`${missing} child${missing === 1 ? "" : "ren"}`);
  }

  return childNames;
}

function getAdultCount(booking, adultNames = []) {
  const explicit = Number(booking?.adult_count);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  if (adultNames.length > 0) return adultNames.length;

  const total = Number(booking?.total_people);
  const children = getChildCount(booking);
  return Number.isFinite(total) && total > 0 ? Math.max(1, total - children) : 1;
}

function getChildCount(booking, childNames = []) {
  const explicit = Number(booking?.child_count);
  if (Number.isFinite(explicit) && explicit >= 0) return explicit;

  return childNames.length;
}

function getTotalAttendees(booking, adultNames = [], childNames = []) {
  const explicit = Number(booking?.total_people);
  const counted = getAdultCount(booking, adultNames) + getChildCount(booking, childNames);
  if (Number.isFinite(explicit) && explicit > 0) return Math.max(explicit, counted);
  return counted;
}

function getBbqPasses(booking) {
  const explicit = Number(normaliseObject(booking?.merch).bbqPasses);
  if (!Number.isFinite(explicit) || explicit < 0) return 0;
  return Math.floor(explicit);
}

function getExtraBadges(booking) {
  const explicit = Number(normaliseObject(booking?.merch).badges);
  if (!Number.isFinite(explicit) || explicit < 0) return 0;
  return Math.floor(explicit);
}

function toPositiveCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

function roomPriceForCapacity(capacity) {
  switch (Number(capacity)) {
    case 1:
      return BOOKING_PRICE_MODEL.room_1_bed_price;
    case 2:
      return BOOKING_PRICE_MODEL.room_2_bed_price;
    case 3:
      return BOOKING_PRICE_MODEL.room_3_bed_price;
    case 4:
      return BOOKING_PRICE_MODEL.room_4_bed_price;
    default:
      return 0;
  }
}

function addPriceRow(rows, label, qty, unitPrice) {
  const count = toPositiveCount(qty);
  if (count <= 0) return;

  rows.push({
    label,
    qty: count,
    unitPrice: Number(unitPrice || 0),
    amount: count * Number(unitPrice || 0),
  });
}

function buildPriceBreakdownRows(booking, { adultCount, childCount, mealCovers } = {}) {
  const rows = [];
  const merch = normaliseObject(booking?.merch, { badges: 0, shirts: {} });

  addPriceRow(
    rows,
    "Adult rally fees",
    adultCount,
    BOOKING_PRICE_MODEL.rally_full_weekend_price
  );
  addPriceRow(
    rows,
    "Child rally fees",
    childCount,
    BOOKING_PRICE_MODEL.child_rally_full_weekend_price
  );

  const accommodation = normaliseAccommodation(booking?.accommodation);
  if (accommodation === "camp") {
    addPriceRow(
      rows,
      "Camp pitches",
      booking?.camp_pitches,
      BOOKING_PRICE_MODEL.camp_pitch_weekend_price
    );
  } else if (accommodation === "camper") {
    addPriceRow(
      rows,
      "Van pitches",
      booking?.camper_pitches,
      BOOKING_PRICE_MODEL.van_pitch_weekend_price
    );
  } else if (accommodation === "room") {
    const countsByCapacity = normaliseArray(booking?.rooms_booked).reduce(
      (acc, room) => {
        const capacity = Number(room?.capacity || 0);
        if (capacity >= 1 && capacity <= 4) {
          acc[capacity] = (acc[capacity] || 0) + 1;
        }
        return acc;
      },
      {}
    );

    [1, 2, 3, 4].forEach((capacity) => {
      addPriceRow(
        rows,
        `${capacity}-bed rooms`,
        countsByCapacity[capacity],
        roomPriceForCapacity(capacity)
      );
    });
  }

  addPriceRow(
    rows,
    "Food packages",
    mealCovers,
    BOOKING_PRICE_MODEL.food_package_price
  );
  addPriceRow(
    rows,
    "BBQ-only passes",
    getBbqPasses(booking),
    BOOKING_PRICE_MODEL.bbq_pass_price
  );

  const shirts = normaliseObject(merch.shirts, {});
  SHIRT_SIZES.forEach((size) => {
    addPriceRow(
      rows,
      `Polo shirts ${size}`,
      shirts[size],
      BOOKING_PRICE_MODEL.polo_shirt_price
    );
  });

  addPriceRow(
    rows,
    "Extra badges",
    getExtraBadges(booking),
    BOOKING_PRICE_MODEL.extra_badge_price
  );

  return rows;
}

async function fetchSvgText(url) {
  const response = await fetch(encodeURI(url));
  if (!response.ok) {
    throw new Error(`Failed to load SVG: ${url}`);
  }
  return response.text();
}

async function fetchImageData(url) {
  const response = await fetch(encodeURI(url));
  if (!response.ok) {
    throw new Error(`Failed to load image: ${url}`);
  }

  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function highlightRoomsInSvg(svgText, roomNames) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, "image/svg+xml");

  const foundRooms = [];

  roomNames.forEach((roomName) => {
    const escapedId = CSS.escape(roomName);
    const el =
      doc.getElementById(roomName) ||
      doc.querySelector(`#${escapedId}`);

    if (el) {
      foundRooms.push(roomName);

      el.setAttribute(
        "style",
        `
          fill: #f6c945 !important;
          stroke: #b7791f !important;
          stroke-width: 4 !important;
          opacity: 1 !important;
        `
      );

      const bboxStroke = doc.createElementNS("http://www.w3.org/2000/svg", "title");
      bboxStroke.textContent = `BOOKED: ${roomName}`;
      el.appendChild(bboxStroke);
    }
  });

  const serializer = new XMLSerializer();
  return {
    svgText: serializer.serializeToString(doc),
    matchedRooms: foundRooms,
  };
}

async function svgToImageData(svgText, width = 1600, height = 1200) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const v = await Canvg.fromString(ctx, svgText);
  await v.render();

  return canvas.toDataURL("image/png");
}

function addFloorPlan(doc, imgData, x, y, maxWidth, maxHeight) {
  const imgProps = doc.getImageProperties(imgData);
  const imgRatio = imgProps.width / imgProps.height;

  let drawW = maxWidth;
  let drawH = drawW / imgRatio;

  if (drawH > maxHeight) {
    drawH = maxHeight;
    drawW = drawH * imgRatio;
  }

  const centredX = x + (maxWidth - drawW) / 2;
  doc.addImage(imgData, "PNG", centredX, y, drawW, drawH, undefined, "FAST");
}

function setText(doc, color = COLORS.text) {
  doc.setTextColor(...color);
}

function setDraw(doc, color = COLORS.border) {
  doc.setDrawColor(...color);
}

function setFill(doc, color = COLORS.white) {
  doc.setFillColor(...color);
}

function drawSectionTitle(doc, title, x, y) {
  const markerX = x + 4;
  const markerY = y - 2;

  setFill(doc, COLORS.navy);
  doc.circle(markerX, markerY, 4, "F");
  setFill(doc, COLORS.white);
  doc.circle(markerX, markerY, 1, "F");

  setText(doc, COLORS.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(title.toUpperCase(), x + 13, y);
}

function fitSingleLine(doc, text, maxWidth) {
  const raw = String(text || "-");
  if (doc.getTextWidth(raw) <= maxWidth) return raw;

  let fitted = raw;
  while (fitted.length > 1 && doc.getTextWidth(`${fitted}...`) > maxWidth) {
    fitted = fitted.slice(0, -1);
  }

  return `${fitted}...`;
}

function drawTable(
  doc,
  rows,
  x,
  y,
  width,
  rowHeight = 11,
  labelWidth = 48,
  options = {}
) {
  const fontSize = options.fontSize || 8.5;
  const lineHeight = options.lineHeight || 4.2;
  const valueWidth = width - labelWidth - 9;
  const rowData = rows.map(([label, value]) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    const valueLines = options.wrapValues
      ? doc.splitTextToSize(String(value || "-"), valueWidth)
      : [fitSingleLine(doc, value, valueWidth)];
    const height = Math.max(rowHeight, 6 + valueLines.length * lineHeight);
    return { label, valueLines, height };
  });
  const height = rowData.reduce((sum, row) => sum + row.height, 0);

  setDraw(doc);
  setFill(doc, COLORS.white);
  doc.roundedRect(x, y, width, height, 2, 2, "FD");
  doc.line(x + labelWidth, y, x + labelWidth, y + height);

  let rowY = y;
  rowData.forEach(({ label, valueLines, height: currentRowHeight }, index) => {
    if (index > 0) {
      doc.line(x, rowY, x + width, rowY);
    }

    setText(doc, COLORS.navy);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    doc.text(label, x + 5, rowY + 7);
    doc.text(valueLines, x + labelWidth + 5, rowY + 7);

    rowY += currentRowHeight;
  });

  return y + height;
}

function drawLeadBookerCard(doc, fullName, x, y, width, height) {
  setDraw(doc, COLORS.black);
  setFill(doc, COLORS.white);
  doc.roundedRect(x, y, width, height, 1.5, 1.5, "FD");

  setText(doc, COLORS.black);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("LEAD BOOKER", x + 5, y + 8);

  doc.setFontSize(19);
  const nameLines = doc.splitTextToSize(String(fullName).toUpperCase(), width - 10);
  doc.text(nameLines, x + 5, y + 20);
}

function drawContentsBox(doc, contentsRows, x, y, width) {
  const rowHeight = 8;
  const headerHeight = 12;
  const height = headerHeight + contentsRows.length * rowHeight + 3;

  setDraw(doc, COLORS.black);
  setFill(doc, COLORS.white);
  doc.roundedRect(x, y, width, height, 1.5, 1.5, "FD");

  setText(doc, COLORS.black);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("CONTENTS", x + 5, y + 8);

  contentsRows.forEach(({ label, qty }, index) => {
    const rowY = y + headerHeight + index * rowHeight;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(String(qty), x + 5, rowY + 5.5);

    doc.setFont("helvetica", "normal");
    doc.text(label, x + 18, rowY + 5.5, { maxWidth: width - 23 });
  });

  return y + height;
}

function drawTotalCost(doc, totalCost, x, y, width, height) {
  setDraw(doc, COLORS.border);
  setFill(doc, COLORS.soft);
  doc.roundedRect(x, y, width, height, 2, 2, "FD");

  setText(doc, COLORS.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("TOTAL COST", x + 5, y + 12);

  doc.setFontSize(18);
  doc.text(formatCurrency(totalCost), x + width - 5, y + 13, { align: "right" });
}

function drawPriceBreakdown(doc, rows, x, y, width) {
  drawSectionTitle(doc, "Cost breakdown", x, y);

  const tableRows = rows.map((row) => [
    row.label,
    `${row.qty} x ${formatCurrency(row.unitPrice)} = ${formatCurrency(row.amount)}`,
  ]);
  const breakdownTotal = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  const tableBottomY = drawTable(
    doc,
    tableRows.length ? tableRows : [["Booked items", "No priced items"]],
    x,
    y + 7,
    width,
    8,
    39,
    { fontSize: 7.6 }
  );

  const totalY = tableBottomY + 3;
  setDraw(doc, COLORS.border);
  setFill(doc, COLORS.soft);
  doc.roundedRect(x, totalY, width, 10, 2, 2, "FD");

  setText(doc, COLORS.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text("BREAKDOWN TOTAL", x + 5, totalY + 6.5);
  doc.text(formatCurrency(breakdownTotal), x + width - 5, totalY + 6.5, {
    align: "right",
  });

  return totalY + 10;
}

async function addBookingSheetPage(
  doc,
  booking,
  { logoData = null, includePriceBreakdown = false } = {}
) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;

  const fullName =
    `${booking.first_name || ""} ${booking.last_name || ""}`.trim() || "-";

  const adultNames = buildAdultNames(booking, fullName);
  const childNames = buildChildNames(booking);
  const adultCount = getAdultCount(booking, adultNames);
  const totalAttendees = getTotalAttendees(booking, adultNames, childNames);
  const mealCovers = getMealCovers(booking);
  const bbqOnlyPasses = getBbqPasses(booking);
  const extraBadges = getExtraBadges(booking);
  const contentsRows = [
    { qty: adultCount, label: "Rally programmes" },
    { qty: adultCount, label: "Treasure hunt route maps" },
    { qty: totalAttendees + extraBadges, label: "Badges" },
    { qty: mealCovers, label: "Blue wristbands (booked meal)" },
    { qty: bbqOnlyPasses, label: "Green wristbands (BBQ only)" },
  ];
  const roomsBooked = normaliseArray(booking.rooms_booked);
  const roomNames = roomsBooked.map((r) => r?.roomName).filter(Boolean);
  const floorPlans = [];

  if (roomNames.length > 0) {
    for (const floor of FLOOR_FILES) {
      const rawSvg = await fetchSvgText(floor.url);
      const highlighted = highlightRoomsInSvg(rawSvg, roomNames);

      if (highlighted.matchedRooms.length === 0) {
        continue;
      }

      floorPlans.push({
        ...floor,
        imgData: await svgToImageData(highlighted.svgText, 1800, 1300),
        matchedRooms: highlighted.matchedRooms,
      });
    }
  }

  setDraw(doc, COLORS.border);
  setFill(doc, COLORS.white);
  doc.rect(0, 0, pageWidth, pageHeight, "FD");

  const leftX = margin;
  const leftW = 96;
  const rightX = leftX + leftW + 8;
  const rightW = pageWidth - rightX - margin;

  if (logoData) {
    const logoSize = 24;
    doc.addImage(
      logoData,
      "PNG",
      leftX + (leftW - logoSize) / 2,
      7,
      logoSize,
      logoSize,
      undefined,
      "FAST"
    );
  }

  setText(doc, COLORS.navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(19);
  doc.text("JAMPOT RALLY 2026", leftX + leftW / 2, 39, { align: "center" });

  setText(doc, COLORS.gold);
  doc.setFontSize(10);
  doc.text("BOOKING SHEET", leftX + leftW / 2, 48, { align: "center" });
  setDraw(doc, COLORS.gold);
  doc.line(leftX + 8, 45, leftX + 30, 45);
  doc.line(leftX + leftW - 30, 45, leftX + leftW - 8, 45);

  setText(doc, COLORS.navy);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    `Generated: ${formatDate(new Date().toISOString())}`,
    leftX + leftW / 2,
    55,
    { align: "center" }
  );

  drawLeadBookerCard(doc, fullName, rightX, 10, rightW, 44);
  drawContentsBox(doc, contentsRows, rightX, 60, rightW);

  drawSectionTitle(doc, "Booking summary", leftX, 67);
  drawTable(
    doc,
    [
      ["Accommodation", normaliseAccommodation(booking.accommodation)],
      ["Rooms", buildRoomsSummary(booking.rooms_booked)],
      ["Meals", buildMealsSummary(booking)],
      ["Early arrival", booking.early_arrival ? "Yes" : "No"],
      ["People", `${totalAttendees} total`],
    ],
    leftX,
    74,
    leftW,
    10.5,
    55
  );

  drawTotalCost(doc, booking.total_cost, leftX, 133, leftW, 18);

  const priceBreakdownBottomY = includePriceBreakdown
    ? drawPriceBreakdown(
        doc,
        buildPriceBreakdownRows(booking, {
          adultCount,
          childCount: getChildCount(booking, childNames),
          mealCovers,
        }),
        leftX,
        162,
        leftW
      )
    : 151;

  drawSectionTitle(doc, "Attendees", rightX, 149);
  const attendeesBottomY = drawTable(
    doc,
    [
      ["Adults", adultNames.length ? adultNames.join(", ") : "-"],
      ["Children", childNames.length ? childNames.join(", ") : "-"],
      ["Merch", buildMerchSummary(booking.merch)],
    ],
    rightX,
    156,
    rightW,
    10.5,
    39,
    { wrapValues: true, lineHeight: 4.4 }
  );

  const separatorY = Math.max(195, attendeesBottomY + 10, priceBreakdownBottomY + 10);
  setDraw(doc, COLORS.border);
  doc.line(margin, separatorY, pageWidth - margin, separatorY);

  if (floorPlans.length > 0) {
    const [firstFloorPlan, ...extraFloorPlans] = floorPlans;
    const inlineHeaderY = separatorY + 12;
    const inlineImgY = inlineHeaderY + 14;
    const inlineMaxH = Math.max(35, 279 - inlineImgY);
    const canInlineFloorPlan = inlineImgY + 35 <= 284;

    if (canInlineFloorPlan) {
      drawSectionTitle(
        doc,
        `${firstFloorPlan.label} - booked rooms highlighted`,
        margin,
        inlineHeaderY
      );

      setText(doc, COLORS.text);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("Rooms on this floor:", margin + 4, inlineHeaderY + 11);

      const roomsText = firstFloorPlan.matchedRooms.join(", ");
      const roomBadgeX = margin + 40;
      const roomBadgeW = Math.min(
        contentWidth - 44,
        Math.max(30, doc.getTextWidth(roomsText) + 8)
      );

      setFill(doc, COLORS.navy);
      doc.roundedRect(roomBadgeX, inlineHeaderY + 5, roomBadgeW, 8, 3, 3, "F");
      setText(doc, COLORS.white);
      doc.setFont("helvetica", "bold");
      doc.text(roomsText, roomBadgeX + roomBadgeW / 2, inlineHeaderY + 10.6, {
        align: "center",
        maxWidth: roomBadgeW - 4,
      });

      addFloorPlan(
        doc,
        firstFloorPlan.imgData,
        margin,
        inlineImgY,
        contentWidth,
        inlineMaxH
      );
    } else {
      extraFloorPlans.unshift(firstFloorPlan);
    }

    for (const floorPlan of extraFloorPlans) {
      doc.addPage();
      setDraw(doc, COLORS.border);
      setFill(doc, COLORS.white);
      doc.rect(0, 0, pageWidth, pageHeight, "FD");

      setText(doc, COLORS.navy);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(`${floorPlan.label} - booked rooms highlighted`, margin, 18);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(
        `Rooms on this floor: ${floorPlan.matchedRooms.join(", ")}`,
        margin,
        25
      );

      addFloorPlan(
        doc,
        floorPlan.imgData,
        margin,
        32,
        contentWidth,
        95
      );
    }
  }
}

function createBookingSheetDoc() {
  return new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });
}

function bookingFileNamePart(booking) {
  const fullName =
    `${booking.first_name || ""} ${booking.last_name || ""}`.trim() || "";

  return fullName.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
}

export async function generateBookingSheetPdf(booking, options = {}) {
  const doc = createBookingSheetDoc();
  const logoData = await fetchImageData(BOOKING_SHEET_LOGO_URL).catch(() => null);
  await addBookingSheetPage(doc, booking, {
    logoData,
    includePriceBreakdown: Boolean(options.includePriceBreakdown),
  });

  const safeName = bookingFileNamePart(booking);
  const filenamePrefix = options.includePriceBreakdown
    ? "priced-booking-sheet"
    : "booking-sheet";
  doc.save(options.filename || `${filenamePrefix}-${safeName || booking.id || "booking"}.pdf`);
}

export async function generateBookingSheetsPdf(
  bookings,
  filename = "paid-booking-sheets.pdf",
  options = {}
) {
  const list = Array.isArray(bookings) ? bookings : [];
  if (list.length === 0) {
    throw new Error("No bookings to include in the PDF");
  }

  const doc = createBookingSheetDoc();
  const logoData = await fetchImageData(BOOKING_SHEET_LOGO_URL).catch(() => null);

  for (let index = 0; index < list.length; index += 1) {
    if (index > 0) {
      doc.addPage();
    }

    await addBookingSheetPage(doc, list[index], {
      logoData,
      includePriceBreakdown: Boolean(options.includePriceBreakdown),
    });
  }

  doc.save(filename);
}
